import { Item } from "@dto/records";
import { TW_NumberCell, TW_NumberCell_editing } from "@utils/twClasses";
import { displayNumber, valueStringOrDefault } from "@utils/types";
import { RefObject, useEffect, useState } from "react";
import { useModelStore, useUserStore } from "src/store";
import { AbstractCell } from "./AbstractCell";
import { CellProps } from "./GridCell";
import { EditRightAccess, hasGlobalRightAccess, ObjectRightType, ViewRightType } from "@dto/Users";


export function NumberCell({ pathKey, field, item, position, updateValue, isEditing, inputRef, blurTriggered }: CellProps) {
    const userRole = useModelStore(state => state.userRole);
    const user = useUserStore(state => state.user);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
    const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);
    const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
    const readOnly = item.readOnly || !hasEditAccess;

    const intRegex = /^-?\d*$/;

    const [text, setText] = useState(valueStringOrDefault(item?.value, ""));
    const wsColor = useModelStore(state => state.wsColor);
    const [lastBlurTriggered, setLastBlurTriggered] = useState<number | null>(null);
    const format = selectedView?.formats?.get(pathKey ?? "") ?? field?.format;
    // console.log("Rendering NumberCell", posToString(position));

    useEffect(() => {
        if (item?.value !== text) {
            setText(valueStringOrDefault(item?.value, ""));
        }
    }, [item]);


    useEffect(() => {
        const newValue = textToValue(text);
        if (isEditing && lastBlurTriggered && blurTriggered && lastBlurTriggered !== blurTriggered && item?.value !== newValue) {
            updateValue(item, newValue);
        }
        setLastBlurTriggered(blurTriggered);
    }, [blurTriggered]);

    const updateLocalValue = (item: Item, value: any) => {
        setText(value || "");
        updateValue(item, value);
    }

    const textToValue = (text: string | null): any => {
        if (!text || text.length === 0) {
            return null;
        }
        if (text === "-") {
            return 0;
        }
        if (text === "Error") {
            return "Error";
        }
        return parseFloat(text);
    }

    const valueToText = (): string => {
        return displayNumber(textToValue(text), format);
    }

    function isTextValid(value: string | null) {
        if (!value || value.length === 0) {
            return true;
        }
        return intRegex.test(value);
    }

    const renderReadOnlyContent = () => {
        const valueStr = valueToText();
        return (
            <div className={TW_NumberCell + (readOnly ? " cursor-not-allowed" : "")}>
                {valueStr}
            </div>
        )
    }

    const renderEditingContent = (
        onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
        onEnterKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void,
        inputRef: RefObject<HTMLInputElement>
    ) => {
        return (
            <input
                className={TW_NumberCell_editing}
                type="text"
                value={text ?? ""}
                onChange={onTextChange}
                onKeyDown={onEnterKeyPress}
                autoFocus={true}
                style={{ borderColor: wsColor }}
                ref={inputRef}
            />
        )
    }

    return <AbstractCell
        item={item}
        position={position}
        updateValue={updateLocalValue}
        renderReadOnlyContent={renderReadOnlyContent}
        renderEditingContent={renderEditingContent}
        textToValue={textToValue}
        isTextValid={isTextValid}
        text={text}
        setText={setText}
        isEditing={isEditing}
        inputRef={inputRef}
        readOnly={readOnly}
    />
}
